import type { JSX } from "react";

import { safeZodPath } from "@impero-com/common/utils/form/safeZodPath";
import { assertNever } from "@impero-com/common/utils/obj";
import { FieldError } from "@impero-com/ui";
import { Select } from "antd";
import { Control, Controller, useController, useWatch } from "react-hook-form";
import { z } from "zod";
import { ParsePayload } from "zod/v4/core";

import { RiskDirectoryBlessedFieldIds } from "api/generated/model";

import { FieldKey } from "../fields";
import { fieldSelectOptions, newFieldType } from "../formData";
import {
    FieldOptions,
    FieldOptionsMode,
    RDStructureOption,
    optionValueSchema,
} from "../options";
import { FormSchema } from "../schema";

import { FieldFormula, formulaSchema } from "./formula";

export const headingSchema = z.object({
    type: z.literal("Heading"),
});
export type HeadingFieldType = z.infer<typeof headingSchema>;

export const textAreaSchema = z.object({
    type: z.literal("TextArea"),
});
export type TextAreaFieldType = z.infer<typeof textAreaSchema>;

export const textInputSchema = z.object({
    type: z.literal("TextInput"),
});
export type TextInputFieldType = z.infer<typeof textInputSchema>;

export const attachedFilesSchema = z.object({
    type: z.literal("AttachedFiles"),
});
export type AttachedFilesFieldType = z.infer<typeof attachedFilesSchema>;

export const numericInputSchema = z.object({
    type: z.literal("NumericInput"),
    formula: formulaSchema,
});
export type NumericInputFieldType = z.infer<typeof numericInputSchema>;

const optionsSchema = z
    .array(optionValueSchema)
    .check((ctx) => validateOptionNamesUniqueness(ctx.value, ctx))
    .check((ctx) => validateRangeMin(ctx.value, ctx));

function validateOptionNamesUniqueness(
    val: Array<RDStructureOption>,
    ctx: ParsePayload
) {
    const optionsValuesMap: Map<string, Array<number>> = new Map();
    val.forEach((option, index) => {
        const optionValue = option.value.toLowerCase();
        const seen = optionsValuesMap.get(optionValue);
        if (seen) {
            optionsValuesMap.set(optionValue, [...seen, index]);
        } else {
            optionsValuesMap.set(optionValue, [index]);
        }
    });
    optionsValuesMap.forEach((indices, valueName) => {
        if (indices.length <= 1) {
            return;
        }
        for (const index of indices) {
            ctx.issues.push({
                code: "custom",
                message:
                    "Option label " +
                    valueName +
                    " is not unique in the options (case insensitive)",
                path: safeZodPath<Array<RDStructureOption>>(`${index}.value`),
                input: ctx.value,
            });
        }
    });
}

// RangeMin of options should cover the whole set of integers
function validateRangeMin(val: Array<RDStructureOption>, ctx: ParsePayload) {
    const optionsRangeMinsMap: Map<number, Array<number>> = new Map();
    val.forEach((option, index) => {
        let rangeMin: number;
        switch (option.rangeMin.type) {
            case "NegInfinity": {
                rangeMin = Number.NEGATIVE_INFINITY;
                break;
            }
            case "Number": {
                rangeMin = option.rangeMin.value;
                break;
            }
            default: {
                return assertNever(option.rangeMin);
            }
        }
        const seen = optionsRangeMinsMap.get(rangeMin);
        if (seen) {
            optionsRangeMinsMap.set(rangeMin, [...seen, index]);
        } else {
            optionsRangeMinsMap.set(rangeMin, [index]);
        }
    });
    if (!optionsRangeMinsMap.has(Number.NEGATIVE_INFINITY)) {
        ctx.issues.push({
            code: "custom",
            message: "Need negative infinity in range min",
            input: ctx.value,
        });
    }
    optionsRangeMinsMap.forEach((indices, rangeMin) => {
        if (indices.length <= 1) {
            return;
        }
        for (const index of indices) {
            ctx.issues.push({
                code: "custom",
                message:
                    "Option range min " +
                    rangeMin +
                    " is not unique in the options",
                path: safeZodPath<Array<RDStructureOption>>(`${index}.value`),
                input: ctx.value,
            });
        }
    });
}

export const checkboxSchema = z.object({
    type: z.literal("CheckboxGroup"),
    options: optionsSchema,
});
export type CheckboxFieldType = z.infer<typeof checkboxSchema>;

export const dropdownSchema = z.object({
    type: z.literal("Dropdown"),
    formula: formulaSchema,
    options: optionsSchema,
});
export type DropdownFieldType = z.infer<typeof dropdownSchema>;

export const radioButtonSchema = z.object({
    type: z.literal("RadioButtonGroup"),
    formula: formulaSchema,
    options: optionsSchema,
});
export type RadioButtonFieldType = z.infer<typeof radioButtonSchema>;

export const fieldTypeSchema = z.discriminatedUnion("type", [
    headingSchema,
    textAreaSchema,
    textInputSchema,
    attachedFilesSchema,
    numericInputSchema,
    checkboxSchema,
    dropdownSchema,
    radioButtonSchema,
]);
export type RDStructureFieldType = z.infer<typeof fieldTypeSchema>;

interface StructureFieldTypeProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    blessedFieldIds: RiskDirectoryBlessedFieldIds;
}

export function StructureFieldType({
    control,
    fieldKey,
    blessedFieldIds,
}: StructureFieldTypeProps) {
    const fieldId = useWatch({ name: `${fieldKey}.fieldId`, control });
    const requiredForAssessment = useWatch({
        name: `${fieldKey}.requiredForAssessment`,
        control,
    });
    const { fieldState: fieldTypeState } = useController({
        name: `${fieldKey}.fieldType`,
        control,
    });

    const mode =
        fieldId === blessedFieldIds.responseId ? "ResponseOptions" : "Normal";
    const impactOrLikelihood = [
        blessedFieldIds.inherentImpactId,
        blessedFieldIds.inherentLikelihoodId,
        blessedFieldIds.residualImpactId,
        blessedFieldIds.residualLikelihoodId,
    ].includes(fieldId);

    return (
        <Controller
            control={control}
            name={`${fieldKey}.fieldType`}
            render={({ field }) => {
                let content: JSX.Element = <></>;
                switch (field.value.type) {
                    case "Heading": {
                        content = <StructureHeading />;
                        break;
                    }
                    case "TextArea": {
                        content = <StructureTextArea />;
                        break;
                    }
                    case "TextInput": {
                        content = <StructureTextInput />;
                        break;
                    }
                    case "AttachedFiles": {
                        content = <StructureAttachedFiles />;
                        break;
                    }
                    case "NumericInput": {
                        content = (
                            <StructureNumericInput
                                control={control}
                                fieldKey={fieldKey}
                            />
                        );
                        break;
                    }
                    case "CheckboxGroup": {
                        content = (
                            <StructureCheckbox
                                control={control}
                                fieldKey={fieldKey}
                                mode={mode}
                            />
                        );
                        break;
                    }
                    case "Dropdown": {
                        content = (
                            <StructureDropdown
                                control={control}
                                fieldKey={fieldKey}
                                mode={mode}
                            />
                        );
                        break;
                    }
                    case "RadioButtonGroup": {
                        content = (
                            <StructureRadioButton
                                control={control}
                                fieldKey={fieldKey}
                                mode={mode}
                            />
                        );
                        break;
                    }
                    default: {
                        return assertNever(field.value);
                    }
                }
                const options =
                    requiredForAssessment ||
                    impactOrLikelihood ||
                    fieldId === blessedFieldIds.responseId
                        ? fieldSelectOptions.map((option) => {
                              return option.value === "Heading" ||
                                  (impactOrLikelihood &&
                                      option.value !== "Dropdown" &&
                                      option.value !== "RadioButtonGroup") ||
                                  (fieldId === blessedFieldIds.responseId &&
                                      option.value !== "Dropdown")
                                  ? { ...option, disabled: true }
                                  : option;
                          })
                        : fieldSelectOptions;
                const onChangeSelect = (newType: string) => {
                    const fieldType = newFieldType(
                        newType as RDStructureFieldType["type"]
                    );
                    if (fieldType === undefined) {
                        throw new Error(
                            "Failed to find default field for " + newType
                        );
                    }
                    field.onChange(fieldType);
                };
                return (
                    <div>
                        <Select
                            options={options}
                            value={field.value.type}
                            onChange={onChangeSelect}
                        />
                        <FieldError
                            error={
                                fieldTypeState.error?.message ??
                                fieldTypeState.error?.root?.message
                            }
                        />
                        {content}
                    </div>
                );
            }}
        />
    );
}

function StructureHeading() {
    return (
        <div>
            <h1>No customization for Heading</h1>
        </div>
    );
}

function StructureTextArea() {
    return (
        <div>
            <h1>No customization for TextArea</h1>
        </div>
    );
}

function StructureTextInput() {
    return (
        <div>
            <h1>No customization for TextInput</h1>
        </div>
    );
}

function StructureAttachedFiles() {
    return (
        <div>
            <h1>No customization for AttachedFiles</h1>
        </div>
    );
}

interface StructureNumericInputProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
}
function StructureNumericInput({
    control,
    fieldKey,
}: StructureNumericInputProps) {
    return (
        <div>
            <FieldFormula control={control} fieldKey={fieldKey} />
        </div>
    );
}

interface StructureCheckboxProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    mode: FieldOptionsMode;
}
function StructureCheckbox({
    control,
    fieldKey,
    mode,
}: StructureCheckboxProps) {
    return (
        <div>
            <FieldOptions control={control} fieldKey={fieldKey} mode={mode} />
        </div>
    );
}

interface StructureDropdownProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    mode: FieldOptionsMode;
}
function StructureDropdown({
    control,
    fieldKey,
    mode,
}: StructureDropdownProps) {
    return (
        <div>
            <FieldFormula control={control} fieldKey={fieldKey} />
            <FieldOptions control={control} fieldKey={fieldKey} mode={mode} />
        </div>
    );
}

interface StructureRadioButtonProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    mode: FieldOptionsMode;
}

function StructureRadioButton({
    control,
    fieldKey,
    mode,
}: StructureRadioButtonProps) {
    return (
        <div>
            <FieldFormula control={control} fieldKey={fieldKey} />
            <FieldOptions control={control} fieldKey={fieldKey} mode={mode} />
        </div>
    );
}
